import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    Chip,
    Button,
    TextField,
    InputAdornment,
    IconButton,
    Collapse,
    CircularProgress,
    Alert,
    Grid,
    Tooltip,
    Divider,
    Stack,
    useMediaQuery,
    useTheme
} from '@mui/material';
import {
    Search as SearchIcon,
    Refresh as RefreshIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    Print as PrintIcon,
    FileDownload as DownloadIcon,
    Clear as ClearIcon,
    Info as InfoIcon,
    Update as UpdateIcon
} from '@mui/icons-material';
import { reconciliationService } from '../../../services/reportService';
import WarehouseFilter from './components/WarehouseFilter';
import DateFilter from './components/DateFilter';
import './components/ReconciliationReport.css';

const ReconciliationReport = ({ onLogout }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));
    const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedWarehouse, setSelectedWarehouse] = useState('');
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [expandedRows, setExpandedRows] = useState({});
    const [totalRecords, setTotalRecords] = useState(0);
    const [lastRefreshDate, setLastRefreshDate] = useState(null);

    const currentDate = new Date();
    const formattedDate = currentDate.toLocaleDateString('id-ID', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
    });
    const formattedDateTime = currentDate.toLocaleString('id-ID', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
    });

    useEffect(() => {
        fetchData();
    }, [selectedWarehouse, startDate, endDate]);

    const fetchData = async () => {
        setLoading(true);
        setError(null);
        try {
            const params = {};
            if (selectedWarehouse) params.warehouse_id = selectedWarehouse;
            if (searchTerm) params.search = searchTerm;
            if (startDate) params.start_date = startDate.toISOString().split('T')[0];
            if (endDate) params.end_date = endDate.toISOString().split('T')[0];

            const response = await reconciliationService.getReconciliationReport(params);
            if (response.data.success) {
                setData(response.data.data || []);
                setTotalRecords(response.data.total_records || 0);
                if (response.data.last_refresh_date) {
                    setLastRefreshDate(response.data.last_refresh_date);
                }
            } else {
                setError(response.data.message || 'Failed to fetch data');
                setData([]);
            }
        } catch (err) {
            setError(err.message || 'An error occurred while fetching data');
            console.error('Error fetching data:', err);
            setData([]);
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = () => {
        fetchData();
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    const handleWarehouseChange = (warehouseId) => {
        setSelectedWarehouse(warehouseId);
        setExpandedRows({});
    };

    const handleDateChange = (start, end) => {
        setStartDate(start);
        setEndDate(end);
        setExpandedRows({});
    };

    const handleClearAllFilters = () => {
        setSearchTerm('');
        setSelectedWarehouse('');
        setStartDate(null);
        setEndDate(null);
        setExpandedRows({});
    };

    const toggleRowExpansion = (index) => {
        setExpandedRows(prev => ({
            ...prev,
            [index]: !prev[index]
        }));
    };

    const getStatusColor = (status) => {
        return status === 'COCOK' ? 'success' : 'error';
    };

    const handlePrint = () => {
        window.print();
    };

    const handleExport = () => {
        const headers = [
            'Kode Item', 'Nama Item', 'No. Lot', 'ED',
            'QTY ORACLE', 'QTY WMS', 'SELISIH', 'STATUS',
            'WAREHOUSE', 'TANGGAL CETAK'
        ];
        const csvRows = [headers.join(',')];

        data.forEach(item => {
            const row = [
                item.kode_item,
                `"${item.nama_item}"`,
                item.no_lot,
                item.ed,
                item.qty_oracle,
                item.qty_wms,
                item.selisih,
                item.status,
                `"${item.warehouse || '-'}"`,
                `"${item.created_at || '-'}"`
            ];
            csvRows.push(row.join(','));
        });

        const csvString = csvRows.join('\n');
        const blob = new Blob(['\uFEFF' + csvString], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = window.URL.createObjectURL(blob);
        link.href = url;
        link.download = `reconciliation_report_${formattedDate.replace(/\//g, '-')}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    };

    const hasActiveFilters = selectedWarehouse || searchTerm || startDate || endDate;

    // Define columns based on screen size
    const getVisibleColumns = () => {
        if (isMobile) {
            return ['kode_item', 'nama_item', 'status', 'detail'];
        }
        if (isTablet) {
            return ['kode_item', 'nama_item', 'no_lot', 'qty_oracle', 'qty_wms', 'selisih', 'status', 'warehouse', 'detail'];
        }
        return ['kode_item', 'nama_item', 'no_lot', 'ed', 'qty_oracle', 'qty_wms', 'selisih', 'status', 'warehouse', 'created_at', 'detail'];
    };

    const visibleColumns = getVisibleColumns();

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box className="reconciliation-page">
            <Box className="reconciliation-container">
                <Paper elevation={0} className="report-paper">
                    {/* Header */}
                    <Box className="report-header">
                        <Typography variant="h6" className="header-subtitle">
                            Laporan Rekonsiliasi Stok Gudang
                        </Typography>
                        <Typography variant="subtitle2" color="textSecondary" className="header-sub">
                            Perbandingan Qty Sistem Oracle vs WMS
                        </Typography>

                        <Grid container spacing={2} className="header-info">
                            <Grid item xs={12} md={5} className="header-actions">
                                <Stack direction="row" spacing={1} justifyContent={{ xs: 'flex-start', md: 'flex-end' }} flexWrap="wrap">
                                    <Button
                                        variant="outlined"
                                        startIcon={<PrintIcon />}
                                        onClick={handlePrint}
                                        size="small"
                                        className="action-button"
                                    >
                                        PRINT
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        startIcon={<DownloadIcon />}
                                        onClick={handleExport}
                                        size="small"
                                        className="action-button"
                                    >
                                        EXPORT CSV
                                    </Button>
                                </Stack>
                            </Grid>
                        </Grid>

                        <Grid container spacing={2} className="header-info">
                            <Grid item xs={12} md={5} className="header-actions">
                                <Stack direction="row" spacing={1} justifyContent={{ xs: 'flex-start', md: 'flex-end' }} flexWrap="wrap">
                                    <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap">
                                        <Tooltip title="Terakhir kali data Oracle di-refresh dari database">
                                            <InfoIcon fontSize="small" color="info" />
                                        </Tooltip>
                                        <Typography variant="caption" color="textSecondary">
                                            <strong>Last Refresh Oracle:</strong>
                                        </Typography>
                                        {lastRefreshDate ? (
                                            <>
                                                <Chip
                                                    icon={<UpdateIcon fontSize="small" />}
                                                    label={lastRefreshDate}
                                                    size="small"
                                                    color="info"
                                                    sx={{
                                                        height: 24,
                                                        fontWeight: 600,
                                                        '& .MuiChip-label': {
                                                            fontSize: '0.7rem',
                                                            padding: '0 10px'
                                                        },
                                                        '& .MuiChip-icon': {
                                                            fontSize: '0.9rem'
                                                        }
                                                    }}
                                                />
                                            </>
                                        ) : (
                                            <Typography variant="caption" color="textSecondary">
                                                Tidak tersedia
                                            </Typography>
                                        )}
                                    </Stack>
                                </Stack>
                            </Grid>
                        </Grid>
                    </Box>

                    

                    <Divider className="header-divider" />

                    {/* Filters */}
                    <Box className="filter-container">
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} md={2.5}>
                                <WarehouseFilter
                                    selectedWarehouse={selectedWarehouse}
                                    onWarehouseChange={handleWarehouseChange}
                                />
                            </Grid>
                            <Grid item xs={12} md={7}>
                                <Box className="search-container">
                                    <TextField
                                        placeholder="Cari berdasarkan Kode Item, Nama Item ..."
                                        variant="outlined"
                                        size="small"
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        onKeyPress={handleKeyPress}
                                        fullWidth
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <SearchIcon fontSize="small" />
                                                </InputAdornment>
                                            ),
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    {searchTerm && (
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => {
                                                                setSearchTerm('');
                                                                setTimeout(handleSearch, 100);
                                                            }}
                                                        >
                                                            <ClearIcon fontSize="small" />
                                                        </IconButton>
                                                    )}
                                                    <IconButton size="small" onClick={handleSearch}>
                                                        <SearchIcon fontSize="small" />
                                                    </IconButton>
                                                </InputAdornment>
                                            )
                                        }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: 1.5,
                                                backgroundColor: '#ffffff',
                                            }
                                        }}
                                    />
                                    <IconButton
                                        onClick={fetchData}
                                        color="primary"
                                        title="Refresh Data"
                                        className="refresh-button"
                                        size="small"
                                        sx={{
                                            border: '1px solid #e0e0e0',
                                            borderRadius: 1.5,
                                            padding: '8px',
                                            '&:hover': {
                                                backgroundColor: '#f5f5f5'
                                            }
                                        }}
                                    >
                                        <RefreshIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                            </Grid>
                        </Grid>

                        {hasActiveFilters && (
                            <Box className="active-filters">
                                <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
                                    <Typography variant="caption" color="textSecondary">
                                        Filter aktif:
                                    </Typography>
                                    {selectedWarehouse && (
                                        <Chip
                                            label={`Warehouse: ${selectedWarehouse}`}
                                            size="small"
                                            onDelete={() => setSelectedWarehouse('')}
                                            sx={{ borderRadius: 1 }}
                                        />
                                    )}
                                    {startDate && endDate && (
                                        <Chip
                                            label={`Tanggal: ${startDate.toLocaleDateString('id-ID')} - ${endDate.toLocaleDateString('id-ID')}`}
                                            size="small"
                                            onDelete={() => {
                                                setStartDate(null);
                                                setEndDate(null);
                                            }}
                                            sx={{ borderRadius: 1 }}
                                        />
                                    )}
                                    {searchTerm && (
                                        <Chip
                                            label={`Cari: ${searchTerm}`}
                                            size="small"
                                            onDelete={() => {
                                                setSearchTerm('');
                                                setTimeout(handleSearch, 100);
                                            }}
                                            sx={{ borderRadius: 1 }}
                                        />
                                    )}
                                    <Chip
                                        label="Clear All"
                                        size="small"
                                        color="primary"
                                        onClick={handleClearAllFilters}
                                        sx={{ ml: 1, borderRadius: 1 }}
                                    />
                                </Stack>
                            </Box>
                        )}
                    </Box>

                    {/* Error Message */}
                    {error && (
                        <Alert severity="error" className="error-alert">
                            {error}
                        </Alert>
                    )}

                    {/* Data Table */}
                    {data.length > 0 ? (
                        <TableContainer component={Paper} className="table-container" elevation={0}>
                            <Table stickyHeader size="small">
                                <TableHead>
                                    <TableRow className="table-header">
                                        {visibleColumns.includes('kode_item') && (
                                            <TableCell className="header-cell">KODE ITEM</TableCell>
                                        )}
                                        {visibleColumns.includes('nama_item') && (
                                            <TableCell className="header-cell">NAMA ITEM</TableCell>
                                        )}
                                        {visibleColumns.includes('no_lot') && (
                                            <TableCell className="header-cell">NO. LOT</TableCell>
                                        )}
                                        {visibleColumns.includes('ed') && (
                                            <TableCell className="header-cell">ED</TableCell>
                                        )}
                                        {visibleColumns.includes('qty_oracle') && (
                                            <TableCell className="header-cell" align="right">QTY ORACLE</TableCell>
                                        )}
                                        {visibleColumns.includes('qty_wms') && (
                                            <TableCell className="header-cell" align="right">QTY WMS</TableCell>
                                        )}
                                        {visibleColumns.includes('selisih') && (
                                            <TableCell className="header-cell" align="right">SELISIH</TableCell>
                                        )}
                                        {visibleColumns.includes('status') && (
                                            <TableCell className="header-cell" align="center">STATUS</TableCell>
                                        )}
                                        {visibleColumns.includes('warehouse') && (
                                            <TableCell className="header-cell">WAREHOUSE</TableCell>
                                        )}
                                        {visibleColumns.includes('detail') && (
                                            <TableCell className="header-cell" align="center">DETAIL</TableCell>
                                        )}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {data.map((item, index) => (
                                        <React.Fragment key={index}>
                                            <TableRow className="data-row" hover>
                                                {visibleColumns.includes('kode_item') && (
                                                    <TableCell className="item-code-cell">{item.kode_item}</TableCell>
                                                )}
                                                {visibleColumns.includes('nama_item') && (
                                                    <TableCell>{item.nama_item}</TableCell>
                                                )}
                                                {visibleColumns.includes('no_lot') && (
                                                    <TableCell>{item.no_lot}</TableCell>
                                                )}
                                                {visibleColumns.includes('ed') && (
                                                    <TableCell>{item.ed}</TableCell>
                                                )}
                                                {visibleColumns.includes('qty_oracle') && (
                                                    <TableCell align="right" className="numeric-cell">
                                                        {item.qty_oracle}
                                                    </TableCell>
                                                )}
                                                {visibleColumns.includes('qty_wms') && (
                                                    <TableCell align="right" className="numeric-cell">
                                                        {item.qty_wms}
                                                    </TableCell>
                                                )}
                                                {visibleColumns.includes('selisih') && (
                                                    <TableCell
                                                        align="right"
                                                        className={`numeric-cell ${parseInt(item.selisih.replace(/\./g, '')) !== 0 ? 'difference' : ''}`}
                                                    >
                                                        {item.selisih}
                                                    </TableCell>
                                                )}
                                                {visibleColumns.includes('status') && (
                                                    <TableCell align="center">
                                                        <Chip
                                                            label={item.status}
                                                            className={`status-chip ${item.status === 'SELISIH' ? 'status-error' : 'status-success'}`}
                                                            size="small"
                                                        />
                                                    </TableCell>
                                                )}
                                                {visibleColumns.includes('warehouse') && (
                                                    <TableCell>
                                                        <Chip
                                                            label={item.warehouse || '-'}
                                                            size="small"
                                                            className="warehouse-chip"
                                                        />
                                                    </TableCell>
                                                )}
                                                {visibleColumns.includes('detail') && (
                                                    <TableCell align="center">
                                                        {item.rack_details && item.rack_details.length > 0 && (
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => toggleRowExpansion(index)}
                                                                className="expand-button"
                                                            >
                                                                {expandedRows[index] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                                            </IconButton>
                                                        )}
                                                    </TableCell>
                                                )}
                                            </TableRow>
                                            {expandedRows[index] && item.rack_details && item.rack_details.length > 0 && (
                                                <TableRow>
                                                    <TableCell colSpan={visibleColumns.length} className="detail-cell">
                                                        <Box className="detail-container">
                                                            <Typography variant="subtitle2" gutterBottom>
                                                                Detail Lokasi Rak
                                                            </Typography>
                                                            <Table size="small" className="detail-table">
                                                                <TableHead>
                                                                    <TableRow>
                                                                        <TableCell>NAMA RAK</TableCell>
                                                                        <TableCell>KODE LEVEL</TableCell>
                                                                        <TableCell>KODE PALET</TableCell>
                                                                        <TableCell align="right">QTY</TableCell>
                                                                    </TableRow>
                                                                </TableHead>
                                                                <TableBody>
                                                                    {item.rack_details.map((rack, rackIndex) => (
                                                                        <TableRow key={rackIndex}>
                                                                            <TableCell>{rack.nama_rak}</TableCell>
                                                                            <TableCell>{rack.kode_level}</TableCell>
                                                                            <TableCell>{rack.kode_palet}</TableCell>
                                                                            <TableCell align="right">{rack.qty}</TableCell>
                                                                        </TableRow>
                                                                    ))}
                                                                </TableBody>
                                                            </Table>
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </React.Fragment>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    ) : (
                        <Box className="empty-state">
                            <Typography variant="body1" color="textSecondary">
                                {error ? 'Error loading data' : 'Tidak ada data yang ditemukan'}
                            </Typography>
                            {!error && (
                                <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                                    Coba ubah filter warehouse, tanggal cetak, atau kata kunci pencarian
                                </Typography>
                            )}
                        </Box>
                    )}

                    {/* Footer */}
                    <Box className="report-footer">
                        <Typography variant="caption" color="textSecondary">
                            <strong>Total Records:</strong> {totalRecords}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                            <strong>Generated:</strong> {formattedDateTime}
                        </Typography>
                    </Box>
                </Paper>
            </Box>
        </Box>
    );
};

export default ReconciliationReport;